import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  StepHeader,
  OnboardingButton,
  OnboardingInput,
  OnboardingAlert,
} from '../components'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { useCheckLeadEmail, useResumeLeadOnboarding } from '@/hooks/api/useLeadOnboarding'
import { ArrowRightIcon } from '@untitledui-icons/react/line'

interface EmailStepProps {
  onNext: () => void
  onBack: () => void
}

export function EmailStep({ onNext, onBack }: EmailStepProps) {
  const { t } = useTranslation()
  const { email, setEmail, hydrateFromLead } = useOnboardingStore()
  const [existingUserError, setExistingUserError] = useState(false)
  const checkEmail = useCheckLeadEmail()
  const resumeOnboarding = useResumeLeadOnboarding()

  const schema = z.object({
    email: z.string().email(t('resources.signup.email.error_invalid')),
  })

  type FormData = z.infer<typeof schema>

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { email },
  })

  const onSubmit = async (data: FormData) => {
    setExistingUserError(false)

    try {
      const result = await checkEmail.mutateAsync(data.email)

      // Only resume if this is a different session (email wasn't already set for current lead)
      if (result.has_lead && data.email !== email) {
        try {
          const lead = await resumeOnboarding.mutateAsync(data.email)
          hydrateFromLead(lead)
          return
        } catch {
          // If resume fails, continue with current lead
        }
      }

      setEmail(data.email)
      onNext()
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } }
      if (axiosError?.response?.status === 409) {
        setExistingUserError(true)
        return
      }
      // Continue anyway if check fails for other reasons
      setEmail(data.email)
      onNext()
    }
  }

  return (
    <div className="flex w-full flex-1 flex-col">
      <StepHeader
        title={t('resources.signup.email.title')}
        subtitle={t('resources.signup.email.subtitle')}
        onBack={onBack}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 flex-col gap-4">
        <OnboardingInput
          {...register('email')}
          type="email"
          label={t('resources.signup.email.label')}
          placeholder={t('resources.signup.email.placeholder')}
          error={errors.email?.message || (existingUserError ? ' ' : undefined)}
        >
          {existingUserError && (
            <OnboardingAlert
              variant="error"
              title={t('resources.signup.email.error_exists_title')}
              className="mt-3"
            >
              {t('resources.signup.email.error_exists_before')}{' '}
              <a href="/login" className="cursor-pointer font-medium underline hover:no-underline">
                {t('resources.signup.email.error_exists_link')}
              </a>{' '}
              {t('resources.signup.email.error_exists_after')}
            </OnboardingAlert>
          )}
        </OnboardingInput>

        <div className="mt-auto pt-4 lg:mt-0 lg:pt-0">
          <OnboardingButton
            type="submit"
            isLoading={isSubmitting || checkEmail.isPending || resumeOnboarding.isPending}
            iconTrailing={ArrowRightIcon}
          >
            {t('resources.signup.common.continue')}
          </OnboardingButton>
        </div>
      </form>
    </div>
  )
}
